import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend)

interface FarmaciaItem {
  nombre: string
  total: number
  porcentaje: number
}

interface ProvinciaItem {
  nombre: string
  total: number
  porcentaje: number
}

interface RegistroItem {
  nombre: string
  provincia: string
  farmacia: string
}

interface Farmacias0503Data {
  titulo: string
  subtitulo: string
  total_puntos: number
  total_farmacias: number
  total_provincias: number
  farmacias: FarmaciaItem[]
  provincias: ProvinciaItem[]
  registros: RegistroItem[]
}

const coloresProvincias = [
  '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#60a5fa',
  '#93c5fd', '#6366f1', '#818cf8', '#4f46e5', '#7c3aed',
  '#8b5cf6', '#a78bfa', '#06b6d4', '#0891b2', '#0e7490',
  '#14b8a6', '#0d9488', '#0f766e', '#2dd4bf',
]

const coloresFarmacias = [
  '#22c55e', '#16a34a', '#15803d', '#166534', '#4ade80',
  '#86efac', '#10b981', '#059669', '#047857', '#34d399',
  '#6ee7b7', '#a7f3d0', '#14b8a6', '#0d9488', '#0f766e',
  '#f59e0b', '#d97706', '#b45309', '#f97316', '#ea580c',
  '#ef4444', '#dc2626', '#8b5cf6',
]

// --- Multi Select Dropdown Component ---
interface MultiSelectDropdownProps {
  tipo: 'farmacia' | 'provincia'
  options: { nombre: string; total: number }[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder: string
  colorClass: string
  badgeColorClass: string
  searchRingClass: string
  checkboxColorClass: string
}

function MultiSelectDropdown({
  tipo,
  options,
  selected,
  onChange,
  placeholder,
  badgeColorClass,
  searchRingClass,
  checkboxColorClass,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus()
      setSearch('')
    }
  }, [isOpen])

  const filteredOptions = useMemo(() => {
    if (!search) return options
    return options.filter(o => o.nombre.toLowerCase().includes(search.toLowerCase()))
  }, [options, search])

  const handleToggle = (nombre: string) => {
    if (selected.includes(nombre)) {
      onChange(selected.filter(s => s !== nombre))
    } else {
      onChange([...selected, nombre])
    }
  }

  // Render badges
  const renderBadges = () => {
    if (selected.length === 0) {
      return <span className="text-gray-500">{placeholder}</span>
    }
    if (selected.length <= 3) {
      return selected.map(s => (
        <span key={s} className={`multi-select-badge ${badgeColorClass}`}>{s}</span>
      ))
    }
    return (
      <>
        {selected.slice(0, 2).map(s => (
          <span key={s} className={`multi-select-badge ${badgeColorClass}`}>{s}</span>
        ))}
        <span className="multi-select-badge bg-gray-100 text-gray-600">+{selected.length - 2} mas</span>
      </>
    )
  }

  return (
    <div className="multi-select-dropdown" ref={dropdownRef}>
      <div
        className={`multi-select-trigger w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm transition-all ${tipo === 'farmacia' ? 'focus:ring-2 focus:ring-green-500 focus:border-green-500' : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="multi-select-badges">
          {renderBadges()}
        </div>
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      <div className={`multi-select-panel bg-white border border-gray-300 rounded-lg mt-1 shadow-lg ${isOpen ? 'open' : ''}`}>
        <div className="p-2 border-b border-gray-200">
          <input
            ref={searchRef}
            type="text"
            placeholder={`Buscar ${tipo}...`}
            className={`w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 ${searchRingClass}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          {filteredOptions.map(opt => (
            <label
              key={opt.nombre}
              className="multi-select-item flex items-center px-4 py-2 text-sm"
              data-value={opt.nombre}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.nombre)}
                onChange={() => handleToggle(opt.nombre)}
                className={`mr-3 rounded ${checkboxColorClass}`}
              />
              <span className="flex-1">{opt.nombre}</span>
              <span className="text-gray-400 ml-2">({opt.total})</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

// --- Main Page ---
export function Farmacias0503Page() {
  const { data, isLoading, error } = useDashReport<Farmacias0503Data>('tonal/farmacias-0503')
  const [selectedFarmacias, setSelectedFarmacias] = useState<string[]>([])
  const [selectedProvincias, setSelectedProvincias] = useState<string[]>([])
  const [farmaciasTableOpen, setFarmaciasTableOpen] = useState(false)
  const [farmaciasExpanded, setFarmaciasExpanded] = useState(false)
  const [provinciasTableOpen, setProvinciasTableOpen] = useState(false)

  // Compute filtered data
  const filteredData = useMemo(() => {
    if (!data) return null

    const registros = data.registros
    let filtrados = registros

    if (selectedFarmacias.length > 0) {
      filtrados = filtrados.filter(r => selectedFarmacias.includes(r.farmacia))
    }
    if (selectedProvincias.length > 0) {
      filtrados = filtrados.filter(r => selectedProvincias.includes(r.provincia))
    }

    const totalFiltrado = filtrados.length

    const farmaciasTotales: Record<string, number> = {}
    const provinciasTotales: Record<string, number> = {}
    filtrados.forEach(r => {
      farmaciasTotales[r.farmacia] = (farmaciasTotales[r.farmacia] || 0) + 1
      provinciasTotales[r.provincia] = (provinciasTotales[r.provincia] || 0) + 1
    })

    const farmacias = Object.entries(farmaciasTotales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({
        nombre,
        total,
        porcentaje: totalFiltrado > 0 ? ((total / totalFiltrado) * 100).toFixed(2) : '0',
      }))

    const provincias = Object.entries(provinciasTotales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({
        nombre,
        total,
        porcentaje: totalFiltrado > 0 ? ((total / totalFiltrado) * 100).toFixed(2) : '0',
      }))

    // Titulo
    let titulo = 'Resumen General'
    if (selectedFarmacias.length > 0 && selectedProvincias.length > 0) {
      const farmLabel = selectedFarmacias.length === 1 ? selectedFarmacias[0] : `${selectedFarmacias.length} farmacias`
      const provLabel = selectedProvincias.length === 1 ? selectedProvincias[0] : `${selectedProvincias.length} provincias`
      titulo = `${farmLabel} - ${provLabel}`
    } else if (selectedFarmacias.length > 0) {
      titulo = selectedFarmacias.length === 1 ? selectedFarmacias[0] : `${selectedFarmacias.length} farmacias seleccionadas`
    } else if (selectedProvincias.length > 0) {
      titulo = selectedProvincias.length === 1 ? selectedProvincias[0] : `${selectedProvincias.length} provincias seleccionadas`
    }

    return {
      totalFiltrado,
      totalFarmacias: Object.keys(farmaciasTotales).length,
      totalProvincias: Object.keys(provinciasTotales).length,
      farmacias,
      provincias,
      titulo,
      provArr: Object.entries(provinciasTotales).sort((a, b) => b[1] - a[1]) as [string, number][],
      farmArr: Object.entries(farmaciasTotales).sort((a, b) => b[1] - a[1]) as [string, number][],
    }
  }, [data, selectedFarmacias, selectedProvincias])

  // Compute available farmacia options (filtered by selected provincias)
  const farmaciaOptions = useMemo(() => {
    if (!data) return []
    let registros = data.registros
    if (selectedProvincias.length > 0) {
      registros = registros.filter(r => selectedProvincias.includes(r.provincia))
    }
    const totales: Record<string, number> = {}
    registros.forEach(r => {
      totales[r.farmacia] = (totales[r.farmacia] || 0) + 1
    })
    return Object.entries(totales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({ nombre, total }))
  }, [data, selectedProvincias])

  // Compute available provincia options (filtered by selected farmacias)
  const provinciaOptions = useMemo(() => {
    if (!data) return []
    let registros = data.registros
    if (selectedFarmacias.length > 0) {
      registros = registros.filter(r => selectedFarmacias.includes(r.farmacia))
    }
    const totales: Record<string, number> = {}
    registros.forEach(r => {
      totales[r.provincia] = (totales[r.provincia] || 0) + 1
    })
    return Object.entries(totales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({ nombre, total }))
  }, [data, selectedFarmacias])

  const handleFarmaciasChange = useCallback((newSelected: string[]) => {
    setSelectedFarmacias(newSelected)
    if (newSelected.length > 0) {
      setProvinciasTableOpen(true)
    }
  }, [])

  const handleProvinciasChange = useCallback((newSelected: string[]) => {
    setSelectedProvincias(newSelected)
  }, [])

  const limpiarFiltros = useCallback(() => {
    setSelectedFarmacias([])
    setSelectedProvincias([])
    setFarmaciasExpanded(false)
  }, [])

  const toggleFarmaciasSection = useCallback(() => {
    if (farmaciasTableOpen) {
      // If expanded, collapse extras
      if (farmaciasExpanded) {
        setFarmaciasExpanded(false)
      } else {
        // Show extras
        setFarmaciasExpanded(true)
      }
    } else {
      setFarmaciasTableOpen(true)
      setFarmaciasExpanded(true)
    }
  }, [farmaciasTableOpen, farmaciasExpanded])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !filteredData) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  // Chart data
  const provinciasChartData = {
    labels: filteredData.provArr.map(p => p[0]),
    datasets: [{
      label: 'Dependientes',
      data: filteredData.provArr.map(p => p[1]),
      backgroundColor: filteredData.provArr.map((_, i) => coloresProvincias[i % coloresProvincias.length]),
      borderRadius: 4,
      maxBarThickness: 40,
    }],
  }

  const farmaciasChartData = {
    labels: filteredData.farmArr.map(f => f[0]),
    datasets: [{
      label: 'Dependientes',
      data: filteredData.farmArr.map(f => f[1]),
      backgroundColor: filteredData.farmArr.map((_, i) => coloresFarmacias[i % coloresFarmacias.length]),
      borderRadius: 4,
      maxBarThickness: 40,
    }],
  }

  const makeChartOptions = (dataArr: [string, number][]) => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { raw: unknown }) => {
            const total = dataArr.reduce((s, p) => s + p[1], 0)
            const raw = ctx.raw as number
            const pct = total > 0 ? ((raw / total) * 100).toFixed(1) : '0'
            return `${raw.toLocaleString('es-EC')} (${pct}%)`
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { callback: (v: string | number) => Number(v).toLocaleString('es-EC') },
      },
      y: {
        ticks: { autoSkip: false, font: { size: 11 } },
      },
    },
  })

  // Farmacia table: show top 10 by default, expandable
  const farmaciasTop10 = filteredData.farmacias.slice(0, 10)
  const displayedFarmacias = farmaciasExpanded ? filteredData.farmacias : farmaciasTop10

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href="https://enterprise.tonalstrategies.com/pricing/" target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Filtro de Farmacia */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Farmacia
              </label>
              <MultiSelectDropdown
                tipo="farmacia"
                options={farmaciaOptions}
                selected={selectedFarmacias}
                onChange={handleFarmaciasChange}
                placeholder="Todas las farmacias"
                colorClass="green"
                badgeColorClass="bg-green-100 text-green-800"
                searchRingClass="focus:ring-green-500"
                checkboxColorClass="text-green-600 focus:ring-green-500"
              />
            </div>

            {/* Filtro de Provincia */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Provincia
              </label>
              <MultiSelectDropdown
                tipo="provincia"
                options={provinciaOptions}
                selected={selectedProvincias}
                onChange={handleProvinciasChange}
                placeholder="Todas las provincias"
                colorClass="blue"
                badgeColorClass="bg-blue-100 text-blue-800"
                searchRingClass="focus:ring-blue-500"
                checkboxColorClass="text-blue-600 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={limpiarFiltros}
              className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors duration-200 border border-gray-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Limpiar Filtros
            </button>
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 lg:p-6 border border-green-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>{filteredData.titulo}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{filteredData.totalFiltrado.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Dependientes</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{filteredData.totalFarmacias}</p>
              <p className="text-sm text-gray-600 mt-1">Farmacias</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{filteredData.totalProvincias}</p>
              <p className="text-sm text-gray-600 mt-1">Provincias</p>
            </div>
          </div>
        </div>

        {/* Graficas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Grafica por Provincia */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Dependientes por Provincia
            </h3>
            <div style={{ position: 'relative', height: '400px' }}>
              <Bar data={provinciasChartData} options={makeChartOptions(filteredData.provArr)} />
            </div>
          </div>

          {/* Grafica por Farmacia */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Dependientes por Farmacia
            </h3>
            <div style={{ position: 'relative', height: '400px' }}>
              <Bar data={farmaciasChartData} options={makeChartOptions(filteredData.farmArr)} />
            </div>
          </div>
        </div>

        {/* Desglose por Farmacia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={toggleFarmaciasSection}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-green-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Farmacia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({filteredData.totalFarmacias} farmacia{filteredData.totalFarmacias !== 1 ? 's' : ''})</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${farmaciasExpanded ? 'rotate-180' : ''}`}
                style={!farmaciasExpanded ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full bg-white text-xs lg:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Farmacia</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Dependientes</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribucion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayedFarmacias.map(farm => (
                  <tr key={farm.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{farm.nombre}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{farm.total}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">{farm.porcentaje}%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="h-2.5 rounded-full barra-farmacia-gradient" style={{ width: `${farm.porcentaje}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{filteredData.totalFiltrado}</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-green-600">100%</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Desglose por Provincia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setProvinciasTableOpen(!provinciasTableOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-blue-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Provincia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({filteredData.totalProvincias} provincia{filteredData.totalProvincias !== 1 ? 's' : ''})</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${provinciasTableOpen ? 'rotate-180' : ''}`}
                style={!provinciasTableOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {provinciasTableOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Provincia</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Dependientes</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribucion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredData.provincias.map(prov => (
                    <tr key={prov.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{prov.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{prov.total}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-blue-600">{prov.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="h-2.5 rounded-full barra-provincia-gradient" style={{ width: `${prov.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{filteredData.totalFiltrado}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-blue-600">100%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Analisis de Dependientes de Farmacia</p>
        </div>
      </div>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .barra-farmacia-gradient {
          background: #4ade80;
        }
        .barra-provincia-gradient {
          background: #93c5fd;
        }
        .multi-select-dropdown {
          position: relative;
        }
        .multi-select-trigger {
          cursor: pointer;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .multi-select-panel {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 50;
          max-height: 280px;
          overflow-y: auto;
          display: none;
        }
        .multi-select-panel.open {
          display: block;
        }
        .multi-select-item {
          cursor: pointer;
          transition: background-color 0.1s;
        }
        .multi-select-item:hover {
          background-color: #f3f4f6;
        }
        .multi-select-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          max-width: calc(100% - 24px);
        }
        .multi-select-badge {
          display: inline-flex;
          align-items: center;
          padding: 1px 8px;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;
        }
      `}</style>
    </div>
  )
}
